

import React, { useState } from 'react';
import { Box, Button, Grid, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from "../components/Footer";
import NewsLetter from '../components/NewsLetter';

const PREVIEW_LIMIT = 260;

const pressReleases = [
  {
    title: 'Ploutas Capital Completes Strategic Funding Transaction Supporting UK Charitable Organisation',
    date: '30 March 2026',
    datePrefix: '',
    body: `Ploutas Capital, a UK-based specialist private lender and arranger of bridging finance, is pleased to announce the completion of a transaction on 13 February, providing funding to a UK charitable organisation that forms part of a broader US-based charitable group and represents an important addition to the UK’s social enterprise landscape.

The transaction presented a complex risk profile, with a number of traditional lenders unable to provide the required support. Ploutas Capital adopted a pragmatic and solutions-oriented approach, structuring and delivering a facility aligned with the specific requirements of the borrower. The firm financed 100% of the transaction costs and fees on a net loan basis, ensuring certainty of execution and efficient completion.

Co-Founder Jamil Akhundov commented: “This transaction reflects our ability to take a commercial and pragmatic view on situations where conventional capital is constrained. We are pleased to support a charitable initiative that plays a meaningful role within the UK social enterprise ecosystem, and to deliver a solution that met the client’s requirements in full.”`,
  },
  {
    title: '£420,000 Bridging Deal Powers Investor’s Next Move',
    date: '18 September 2025',
    image: '/images/news/bridging-investor-next-move.jpg',
    body: `Speed made the difference.

A property investor unlocked £420,000 in equity through a fast-tracked bridging facility, turning a static Buy-to-Let asset into immediate capital — right when it mattered most.

With traditional lenders slowed by process and policy, a tailored, asset-backed solution delivered funding in weeks, not months. No delays. No missed opportunity.

The result? A time-sensitive commercial move secured, and momentum maintained.

In markets where hesitation costs deals, bridging finance continues to give investors the one thing they can’t afford to lose: time.

Jamil Akhundov commented: "In this market, opportunities don’t wait. Our role is to make sure capital doesn’t either. This transaction shows how speed and structure can unlock real value for our clients."`,
  },
  {
    title: '£276,500 Bridging Facility Supports Strategic Refinance and Business Liquidity',
    date: '12 September 2025',
    image: '/images/news/bridging-refinance-liquidity.jpg',
    body: `A UK property investment company has secured a £276,500 short-term bridging facility to refinance an existing position and release additional business-purpose liquidity from a mixed-use asset in West London.

The transaction was structured as a first-charge bridge loan at 70% LTV, providing a fast and flexible funding solution in a situation where traditional lenders were unable to respond within required timelines.

The deal was considered non-standard for mainstream lending due to its specialist structure, including enhanced security provisions and conservative valuation parameters.

The funding enabled the borrower to stabilise their position, refinance existing exposure, and access working capital without disruption to their broader investment strategy.

This transaction highlights the continued role of bridging finance in supporting complex refinancing scenarios where timing, structure, and certainty of execution are critical.

Jamil Akhundov commented: "This transaction reflects exactly what bridging finance is designed for — certainty, speed, and the ability to solve complexity when time is limited."`,
  },
  {
    title: '£206,500 Bridging Facility Enables Strategic Dual-Asset Refinance in London',
    date: '12 September 2025',
    image: '/images/news/dual-asset-refinance-london.jpg',
    body: `A UK property investment firm has secured a £206,500 short-term bridging facility as part of a wider dual-asset refinancing strategy.

The facility formed part of a structured solution spanning multiple assets, designed to release equity and provide short-term liquidity while maintaining control.

Given the short-term nature of the funding, the transaction sat outside traditional high-street lending criteria.

The funding provided the borrower with flexibility to stabilise their position and progress towards longer-term refinancing.

This transaction reinforces the role of bridging finance in supporting complex portfolio restructuring.

Jamil Akhundov commented: "This is a good example of how structured bridging solutions can bring clarity to complex, multi-asset situations."`,
  },
  {
    title: '£500,000 Structured Bridging Facility Unlocks Value in Complex Northern Ireland Land Portfolio',
    date: '12 July 2025',
    image: '/images/news/northern-ireland-land-portfolio.jpg',
    body: `A UK-based borrower has secured a £500,000 bridging facility backed by a diversified portfolio of agricultural and development land across Northern Ireland.

The transaction supported liquidity against a multi-asset security package spanning farmland and development land.

Traditional lenders typically struggle with this type of profile due to complexity and mixed land use.

This facility was structured with a first-charge security position and multiple exit pathways.

The result was the successful deployment of capital into a market segment often underserved by mainstream lending.

Jamil Akhundov commented: "This was a classic example of where complexity becomes opportunity."`,
  },
];

function PressReleaseArticle({ release }) {
  const [expanded, setExpanded] = useState(false);
  const hasMore = release.body.length > PREVIEW_LIMIT;
  const visibleBody = expanded || !hasMore
    ? release.body
    : `${release.body.slice(0, PREVIEW_LIMIT).trimEnd()}...`;
  const dateText = release.datePrefix === ''
    ? release.date
    : `${release.datePrefix || 'Completed'}: ${release.date}`;

  return (
    <Box
      component="article"
      sx={{
        border: '1px solid rgba(10, 20, 69, 0.12)',
        borderRadius: 1,
        overflow: 'hidden',
        backgroundColor: '#fff',
        boxShadow: '0 12px 36px rgba(10, 20, 69, 0.08)',
      }}
    >
      {release.image && (
        <Box
          component="img"
          src={release.image}
          alt={release.title}
          sx={{
            width: '100%',
            height: { xs: 220, md: 320 },
            display: 'block',
            objectFit: 'cover',
          }}
        />
      )}
      <Box sx={{ p: { xs: 3, md: 4 } }}>
        <Typography sx={{ color: '#B6915D', fontWeight: 800, mb: 1 }}>
          {dateText}
        </Typography>
        <Typography
          component="h3"
          sx={{
            color: '#1E325C',
            fontSize: { xs: '1.45rem', md: '1.9rem' },
            fontWeight: 800,
            lineHeight: 1.2,
            mb: 2,
          }}
        >
          {release.title}
        </Typography>
        <Box sx={{ position: 'relative' }}>
          <Typography
            component="div"
            color="#333333"
            sx={{
              fontSize: '1rem',
              lineHeight: 1.8,
              whiteSpace: 'pre-line',
            }}
          >
            {visibleBody}
          </Typography>
          {hasMore && !expanded && (
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                height: 72,
                pointerEvents: 'none',
                background: 'linear-gradient(to bottom, rgba(255,255,255,0), #fff 82%)',
              }}
            />
          )}
        </Box>
        {hasMore && (
          <Button
            onClick={() => setExpanded((value) => !value)}
            sx={{
              mt: 2,
              p: 0,
              minWidth: 'auto',
              color: '#B6915D',
              fontWeight: 800,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'transparent',
                color: '#1E325C',
              },
            }}
          >
            {expanded ? 'Show Less' : 'Read More'}
          </Button>
        )}
      </Box>
    </Box>
  );
}

export default function News() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -40 }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
    >
      <Navbar />
      <Box
        sx={{
          height: '50vh',
          width: '100%',
          backgroundColor: "rgba(10, 20, 69, 0.85)",
          backgroundImage: "url('/images/news.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundBlendMode: "multiply",
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          flexDirection: 'column',
          textAlign: 'center',
          pl: { xs: "0px", md: "110px" },
          px: 3,
        }}
      >
        <Typography variant='h2'>
          News & Insights
        </Typography>
        {/* <Typography variant='h5'>
          Join Our Team
        </Typography> */}
      </Box>

      <Typography sx={{ mt: 8, ml: { xs: "0", md: "220px" }, textAlign: 'center', fontWeight: 800, fontSize: { xs: "2rem", md: "3rem" } }}>
        Ploutas Capital&nbsp;&mdash; Case&nbsp;Studies
      </Typography>

      <Grid container spacing={2} sx={{ mt: 8, mb: 4, px: { xs: 2, md: 12 }, ml: { xs: 0, md: "220px" } }}>
        <Grid item size={{ xs: 12, lg: 4 }}>
          <Box id="private-lending" sx={{ pl: { xs: 0, lg: 2 }, pr: { xs: 0, lg: 2 } }}>
            <Typography variant="h5" sx={{ mb: 2, color: '#B6915D', fontWeight: 'bold', minHeight: { xs: 0, lg: '64px' } }}>
              Representative Transactions
            </Typography>
            • Overseas client acquiring portfolio of residential assets
            <ul>
              <li>Budget: £1,000,000</li>
              <li>Delivery: Under 1 week</li>
            </ul>

            {/* • Forest development site financing
            <ul>
              <li>Budget: £600,000</li>
              <li>Delivery: 3 working days</li>
            </ul> */}

            • Bridge to development of large urban development project
            <ul>
              <li>Budget: €250,000,000</li>
              <li>Delivery: Confidential</li>
            </ul>

            {/* • Luxury mansion in London
            <ul>
              <li>Budget: £8,000,000</li>
              <li>Delivery: Confidential</li>
            </ul>

            • Financed retirement home acquisition and development (80% LTC)
            <ul>
              <li>Budget: £1,300,000</li>
              <li>Delivery: Confidential</li>
            </ul>

            • PBSA development loan (debt + equity: 98% LTC)
            <ul>
              <li>Budget: £15,000,000</li>
              <li>Delivery: Confidential</li>
            </ul>
            • Financed HMO conversion deal, covering 90% of cost
            <ul>
              <li>Budget: £500,000</li>
              <li>Delivery: Confidential</li>
            </ul>
            • Land acquisition deal financing
            <ul>
              <li>Budget: £1,000,000</li>
              <li>Delivery: 2 weeks</li>
            </ul>
            • Exit of student project financing (75% LTV)
            <ul>
              <li>Budget: £7,000,000</li>
              <li>Delivery: Confidential</li>
            </ul>
            • Second-charge bridge on building development site
            <ul>
              <li>Budget: £315,000</li>
              <li>Delivery: Confidential</li>
            </ul>
            • Industrial asset with development cost financing
            <ul>
              <li>Budget: £4,800,000</li>
              <li>Delivery: Confidential</li>
            </ul> */}
          </Box>
        </Grid>
        <Grid item size={{ xs: 12, lg: 4 }}>
          <Box id="private-lending" sx={{ pl: { xs: 0, lg: 2 }, pr: { xs: 0, lg: 2 } }}>
            <Typography variant="h5" sx={{ mb: 2, color: '#B6915D', fontWeight: 'bold', minHeight: { xs: 0, lg: '64px' } }}>
               UK Auction Finance Examples
            </Typography>
            • 3-bedroom auction property in Surrey
            <ul>
              <li>Budget: £350,000 + £100,000 refurbishment</li>
              <li>Delivery: Exited via mortgage</li>
            </ul>
            • Portfolio of residential properties
            <ul>
              <li>Budget: £1,300,000</li>
              <li>Delivery: Sold individually in 6 months, ~20% profit</li>
            </ul>
            {/* • Leisure development site acquisition
            <ul>
              <li>Budget: £1,000,000</li>
              <li>Delivery: Plot sales over 12 months ~£1,200,000</li>
            </ul> */}
          </Box>
        </Grid>
        <Grid item size={{ xs: 12, lg: 4 }}>
          <Box id="private-lending" sx={{ pl: { xs: 0, lg: 2 }, pr: { xs: 0, lg: 2 } }}>
            <Typography variant="h5" sx={{ mb: 2, color: '#B6915D', fontWeight: 'bold', minHeight: { xs: 0, lg: '64px' } }}>
              Luxury Finance Examples
            </Typography>
            • Refinanced fleet of luxury cars
            <ul>
              <li>Value: £8.3 million</li>
              <li>Delivery: 2021</li>
            </ul>
            {/* • Assisted purchase of luxury yacht
            <ul>
              <li>Value: US$60 million</li>
              <li>Delivery: Closed in 3 weeks (2018)</li>
            </ul> */}
            • Financed super luxury property in Monaco
            <ul>
              <li>Value: €100 million</li>
              <li>Delivery: 2021</li>
            </ul>
            {/* • Financed pre-owned artwork (4 items)
            <ul>
              <li>Value: US$3–5 million each</li>
              <li>Delivery: 2016</li>
            </ul> */}
          </Box>
        </Grid>

      </Grid>

      <NewsLetter />

      {/* Press Releases */}
      <Typography sx={{ mt: 8, ml: { xs: "0", md: "220px" }, textAlign: 'center', fontWeight: 800, fontSize: { xs: "2rem", md: "3rem" } }}>
        PRESS RELEASE
      </Typography>

      <Box
        id="press-release"
        sx={{
          display: 'grid',
          gap: 4,
          ml: { md: '220px' },
          px: { xs: 2, md: 12 },
          py: 6,
        }}
      >
        {pressReleases.map((release) => (
          <PressReleaseArticle key={release.title} release={release} />
        ))}
      </Box>

      {/* <Grid container spacing={2} sx={{ mt: 8, mb: 4, px: { xs: 2, md: 12 }, ml: { xs: 0, md: "220px" } }}>
        <Grid item size={{ xs: 12, lg: 4 }}>
          <Box id="private-lending" sx={{ pl: { xs: 0, lg: 2 }, pr: { xs: 0, lg: 2 } }}>
            <Typography variant="h2">
              Ploutas Capital Completes Strategic Funding Transaction Supporting UK Charitable Organisation
            </Typography>
            • Overseas client acquiring portfolio of residential assets
          </Box>
        </Grid>
      </Grid> */}

      <Footer />
    </motion.div>
  );
}
